import type { GameManifest } from '@adgf/shared';
import { GameStatus } from '@adgf/shared';
import type { GameRuntimeFactory } from '@adgf/game-engine';
// Statically import the game module so its runtime factory is available
// without relying on runtime dynamic import of bare module specifiers
// (which Vite cannot always resolve in dev mode). The registry still
// provides dynamic *selection* of which game to load based on slug.
import reactionManifest from '@adgf/game-30-second-reaction';
import { createRuntime as reactionCreateRuntime } from '@adgf/game-30-second-reaction';
// M2 dynamic games — registered alongside the M1 reaction game.
import memoryManifest from '@adgf/game-memory-challenge';
import { createRuntime as memoryCreateRuntime } from '@adgf/game-memory-challenge';
import clickerManifest from '@adgf/game-one-tap-clicker';
import { createRuntime as clickerCreateRuntime } from '@adgf/game-one-tap-clicker';
import puzzleManifest from '@adgf/game-mini-puzzle';
import { createRuntime as puzzleCreateRuntime } from '@adgf/game-mini-puzzle';
import match3Manifest from '@adgf/game-match-3';
import { createRuntime as match3CreateRuntime } from '@adgf/game-match-3';
import dailyTileMatchManifest from '@adgf/game-daily-tile-match';
import { createRuntime as dailyTileMatchCreateRuntime } from '@adgf/game-daily-tile-match';
import sniperRangeManifest from '@adgf/game-sniper-range';
import { createRuntime as sniperRangeCreateRuntime } from '@adgf/game-sniper-range';

/**
 * Entrypoints with this prefix are handled by the legacy M0 route
 * (/games/:slug) and have no dynamic runtime to load here.
 */
const LEGACY_PREFIX = 'legacy:';

/**
 * GameRegistry — client-side registry of all games available in the web app.
 *
 * It holds the GameManifest for every registered game and returns the
 * matching GameRuntime factory on demand. No business rules are hardcoded
 * in the UI; everything comes from manifests.
 *
 * Future M2+: factories can be loaded via true dynamic import() once the
 * game modules are served from a CDN or static bundle.
 */
export class GameRegistry {
  private readonly manifests = new Map<string, GameManifest>();
  private readonly factories = new Map<string, GameRuntimeFactory>();

  /** Register a game manifest and its runtime factory. */
  register(manifest: GameManifest, factory?: GameRuntimeFactory): void {
    this.manifests.set(manifest.slug, manifest);
    if (factory) {
      this.factories.set(manifest.slug, factory);
    }
  }

  /** Look up a manifest by slug, or undefined if not registered. */
  getBySlug(slug: string): GameManifest | undefined {
    return this.manifests.get(slug);
  }

  /**
   * Return the version string (semver) for a slug, or undefined if the
   * game is not registered. Used by the version pinning UI.
   */
  getVersion(slug: string): string | undefined {
    return this.manifests.get(slug)?.version;
  }

  /**
   * Return the list of versions available for a slug.
   *
   * M2: each bundled game ships exactly one version (the one in its
   * manifest), so this returns a single-element array. When games are
   * served from a versioned CDN this becomes the full rollback history.
   */
  listVersions(slug: string): string[] {
    const manifest = this.manifests.get(slug);
    return manifest ? [manifest.version] : [];
  }

  /** Return all registered manifests (insertion order preserved). */
  list(): GameManifest[] {
    return [...this.manifests.values()];
  }

  /**
   * Return the runtime factory for a slug, or null for legacy games that
   * have no dynamic runtime (handled by the classic /games/:slug route).
   */
  async loadRuntime(slug: string): Promise<GameRuntimeFactory | null> {
    const manifest = this.getBySlug(slug);
    if (!manifest || !manifest.entrypoint) return null;
    if (manifest.entrypoint.startsWith(LEGACY_PREFIX)) return null;
    return this.factories.get(slug) ?? null;
  }
}

/**
 * Build the pre-populated default registry.
 *
 * - demo-clicker: the M0 legacy game (handled by GamePage, not dynamically
 *   loaded). Registered as PUBLISHED so it stays in the public hall.
 * - 30-second-reaction: its own manifest is DRAFT; for M1 local testing we
 *   promote it to PUBLISHED so it appears in the hall and is reachable via
 *   the dynamic /game/:slug route.
 */
function buildDefaultRegistry(): GameRegistry {
  const registry = new GameRegistry();

  registry.register({
    id: '11111111-1111-1111-1111-111111111111',
    slug: 'demo-clicker',
    title: 'Demo Clicker',
    description: 'A simple clicker game to demonstrate the Phaser runtime environment.',
    version: '0.1.0',
    template_id: null,
    runtime: 'phaser',
    status: GameStatus.PUBLISHED,
    difficulty: 'easy',
    duration: 0,
    features: [],
    entrypoint: `${LEGACY_PREFIX}demo-clicker`,
    analytics_schema: {},
  } satisfies GameManifest);

  // Promote the local manifest from DRAFT to PUBLISHED for M1 testing.
  registry.register(
    {
      ...reactionManifest,
      status: GameStatus.PUBLISHED,
    },
    reactionCreateRuntime,
  );

  // M2 dynamic games. Their manifests ship as DRAFT; for local testing we
  // promote them to PUBLISHED (same pattern as the reaction game) so they
  // appear in the public hall and are reachable via /game/:slug.
  registry.register(
    { ...memoryManifest, status: GameStatus.PUBLISHED },
    memoryCreateRuntime,
  );
  registry.register(
    { ...clickerManifest, status: GameStatus.PUBLISHED },
    clickerCreateRuntime,
  );
  registry.register(
    { ...puzzleManifest, status: GameStatus.PUBLISHED },
    puzzleCreateRuntime,
  );
  registry.register(
    { ...match3Manifest, status: GameStatus.PUBLISHED },
    match3CreateRuntime,
  );
  registry.register(
    { ...dailyTileMatchManifest, status: GameStatus.PUBLISHED },
    dailyTileMatchCreateRuntime,
  );
  registry.register(
    { ...sniperRangeManifest, status: GameStatus.PUBLISHED },
    sniperRangeCreateRuntime,
  );

  return registry;
}

/** Singleton registry used across the web app. */
export const gameRegistry: GameRegistry = buildDefaultRegistry();

export default gameRegistry;
